/**
 * AI Chat Assistant Component
 * Provides an AI-powered chat interface for sketch editing
 */

'use client';

import { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, X, Loader2, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createAIService } from '@/lib/ai-service';
import { SketchAgent, AgentContext } from '@/lib/sketch-agent';
import { AIProvider } from '@/types/ai';
import { ControlDefinition } from '@/types/api';
import { SiteVariant } from '@/lib/site-config';
import './AIChat.scss';

interface AIChatProps {
  // AI Configuration
  aiProvider: AIProvider;
  aiApiKey: string;
  aiApiEndpoint: string;
  aiModel: string;
  
  // Sketch Context
  sketchCode: string;
  sketchErrors: string | null;
  controls: ControlDefinition[];
  siteVariant: SiteVariant;
  
  // Callbacks
  onCodeUpdate: (code: string, explanation: string) => void;
  onControlsUpdate: (controls: ControlDefinition[]) => void;
  onClose: () => void;
}

interface Message {
  role: 'user' | 'assistant' | 'system' | 'error';
  content: string;
  timestamp: Date;
}

export default function AIChat({
  aiProvider,
  aiApiKey,
  aiApiEndpoint,
  aiModel,
  sketchCode,
  sketchErrors,
  controls,
  siteVariant,
  onCodeUpdate,
  onControlsUpdate,
  onClose,
}: AIChatProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hi! I\'m your AI assistant. I can help you write, debug, and improve your sketch. What would you like to do?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [agent, setAgent] = useState<SketchAgent | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Initialize agent (fetch system prompt from API)
  useEffect(() => {
    let cancelled = false;
    
    const initAgent = async () => {
      try {
        setIsInitializing(true);
        
        // Fetch system prompt from API
        const response = await fetch(`/api/ai/system-prompt?variant=${siteVariant}`);
        if (!response.ok) {
          throw new Error('Failed to load AI system prompt');
        }
        
        const { systemPrompt } = await response.json();
        
        if (cancelled) return;
        
        const aiService = createAIService(aiProvider, aiApiKey, aiApiEndpoint, aiModel);
        
        const context: AgentContext = {
          sketchCode,
          sketchErrors,
          controls,
          siteVariant,
          systemPrompt,
          onCodeUpdate: (code, explanation) => {
            onCodeUpdate(code, explanation);
            addMessage('assistant', `✓ Updated sketch: ${explanation}`);
          },
          onControlsUpdate: (newControls) => {
            onControlsUpdate(newControls);
            addMessage('assistant', `✓ Updated controls`);
          },
          onError: (error) => {
            addMessage('error', error);
          }
        };

        const newAgent = new SketchAgent(aiService, context);
        setAgent(newAgent);
      } catch (error) {
        console.error('Failed to initialize AI agent:', error);
        if (!cancelled) {
          addMessage('error', 'Failed to initialize AI assistant. Please check your API settings.');
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    };
    
    initAgent();
    
    return () => {
      cancelled = true;
    };
  }, [aiProvider, aiApiKey, aiApiEndpoint, aiModel, siteVariant]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addMessage = (role: Message['role'], content: string) => {
    setMessages(prev => [...prev, { role, content, timestamp: new Date() }]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || !agent || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    addMessage('user', userMessage);
    setIsLoading(true);

    try {
      const response = await agent.processMessage(userMessage);
      addMessage('assistant', response);
    } catch (error) {
      console.error('Chat error:', error);
      addMessage('error', `Failed to get response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="ai-chat">
      <div className="ai-chat-header">
        <div className="header-title">
          <Sparkles size={18} />
          <span>AI Assistant</span>
          <span className="model-badge">{aiModel}</span>
        </div>
        <button onClick={onClose} className="close-button" aria-label="Close chat">
          <X size={18} />
        </button>
      </div>

      <div className="ai-chat-messages">
        {isInitializing ? (
          <div className="message message-assistant loading">
            <div className="message-content">
              <Loader2 size={16} className="spinner" />
              <span>Initializing AI assistant...</span>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div key={index} className={`message message-${message.role}`}>
                <div className="message-content">
                  {message.role === 'error' && (
                    <div className="message-icon">
                      <AlertCircle size={16} />
                    </div>
                  )}
                  <div className="message-text">
                    {message.role === 'assistant' || message.role === 'system' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    ) : (
                      message.content.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                      ))
                    )}
                  </div>
                </div>
                <div className="message-timestamp">
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="message message-assistant loading">
                <div className="message-content">
                  <Loader2 size={16} className="spinner" />
                  <span>Thinking...</span>
                </div>
              </div>
            )}
          </>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="ai-chat-input">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isInitializing ? "Initializing..." : "Ask me anything about your sketch..."}
          rows={1}
          disabled={isLoading || !agent || isInitializing}
          className="input-field"
        />
        <button
          type="submit"
          disabled={isLoading || !input.trim() || !agent || isInitializing}
          className="send-button"
          aria-label="Send message"
        >
          <Send size={18} />
        </button>
      </form>

      <div className="ai-chat-footer">
        <p>AI responses may contain errors. Always review generated code.</p>
      </div>
    </div>
  );
}
